import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useCartButtonSummary } from './ChatTripPlannerPage/hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from './ChatTripPlannerPage/hooks/useFetchCartPanelOffers'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import CartButton from './ChatTripPlannerPage/components/CartButton'
import DownloadItineraryButton from './ChatTripPlannerPage/components/DownloadItineraryButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import {
  TripPlannerStickyHeader,
  TripPlannerHeaderIconButton,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import { useTripPlannerMenuDrawer } from './ChatTripPlannerPage/hooks/useTripPlannerMenuDrawer'

const NAVIGATION_HEIGHT = rem(48)

const HeaderLayout = styled(TripPlannerStickyHeader)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: 'navigation actions';
  column-gap: ${rem(8)};
  height: ${NAVIGATION_HEIGHT};
  padding-block: ${rem(4)};
  flex-shrink: 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};

  @media print {
    display: none;
  }
`

const HeaderNavigation = styled(LeftContent)`
  grid-area: navigation;
`

const HeaderActions = styled(RightContent)`
  grid-area: actions;

  margin-inline-start: 0;
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const HeaderLogo = styled(TripPlannerStyledLogo)`
  width: ${rem(28)};
  height: ${rem(28)};
`

interface Props {
  chatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  isRewindMode: boolean
  onDevModeToggle: () => void
  onRewindModeToggle: () => void
  className?: string
}

function TripPlannerHeader({
  chatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  isRewindMode,
  onDevModeToggle,
  onRewindModeToggle,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const eventBus = useTripPlannerEventBus()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  useFetchCartPanelOffers(itineraryData, true)
  const cartSummary = useCartButtonSummary(itineraryData, isLoggedIn)

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const handleOpenDrawer = useTripPlannerMenuDrawer({
    isLoggedIn,
    isStaff,
    isDevMode,
    isRewindMode,
    onStartNewChat,
    onChatSelect,
    onChatDeleted,
    onDevModeToggle,
    onRewindModeToggle,
    activeChatId: chatId,
    hasActiveChatItinerary: hasItineraryData,
  })

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const showCartButton = !!itineraryData

  const rightContent = useMemo(() => (
    <Group direction="horizontal" gap={8} verticalAlign="center">
      {showCartButton && <CartButton
        itemCount={cartSummary.bookableItemCount}
        totalPrice={cartSummary.estimatedTotal}
        isLoading={cartSummary.isLoading}
        onClick={handleCartClick}
      />}
      {!isLoggedIn && (
        <TextButton kind="secondary" size="medium" onClick={handleLogin}>
          {loginButtonText}
        </TextButton>
      )}
      {hasItineraryData && <DownloadItineraryButton chatId={chatId} tripActions />}
    </Group>
  ), [isLoggedIn, hasItineraryData, chatId, handleLogin, loginButtonText, showCartButton, cartSummary.bookableItemCount, cartSummary.estimatedTotal, cartSummary.isLoading, handleCartClick])

  return (
    <HeaderLayout className={className}>
      <HeaderNavigation>
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <TripPlannerHeaderIconButton
            onClick={handleOpenDrawer}
            aria-label={openMenuAriaLabel}
          >
            <LineBarsIcon />
          </TripPlannerHeaderIconButton>
          {chatId === undefined && (
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <LogoWrapper>
                <HeaderLogo />
              </LogoWrapper>
              <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
            </Group>
          )}
        </Group>
      </HeaderNavigation>
      {rightContent && (
        <HeaderActions>
          {rightContent}
        </HeaderActions>
      )}
    </HeaderLayout>
  )
}

export default TripPlannerHeader
